import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * DiamondViewer3D Component
 *
 * Photorealistic 3D diamond viewer with:
 * - Real-time 360° rotation
 * - Accurate diamond faceting and dispersion
 * - Multiple lighting scenarios
 * - Size reference overlay
 * - AR try-on capability
 */
import React, { useRef, useState, useMemo, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Environment, Sparkles } from '@react-three/drei';
import * as THREE from 'three';
import styled from 'styled-components';
import { getDiamondGeometry } from './DiamondGeometry';
import { createDataDrivenDiamond } from './DataDrivenDiamond';
// Diamond shapes - facet configurations
const DIAMOND_SHAPES = {
    round: { radius: 1, segments: 32, facets: 58 },
    princess: { width: 1, height: 1, depth: 0.6, facets: 76 },
    emerald: { width: 1, height: 1.4, depth: 0.6, facets: 58 },
    oval: { radiusX: 1, radiusY: 1.3, segments: 32, facets: 58 },
    cushion: { width: 1, height: 1, depth: 0.6, cornerRadius: 0.2, facets: 64 },
    pear: { top: 1.3, bottom: 1, width: 1, facets: 58 },
    marquise: { length: 1.6, width: 1, facets: 58 },
    heart: { width: 1, height: 1, depth: 0.6, facets: 59 },
    asscher: { width: 1, height: 1, depth: 0.65, facets: 74 },
    radiant: { width: 1, height: 1.3, depth: 0.6, facets: 70 }
};
// Lighting scenarios
const LIGHTING_MODES = {
    studio: {
        intensity: 1.2,
        envIntensity: 0.8,
        ambient: '#ffffff',
        key: '#ffffee',
        fill: '#bbccff'
    },
    sunlight: {
        intensity: 1.5,
        envIntensity: 1.0,
        ambient: '#fff5e6',
        key: '#ffffaa',
        fill: '#aaccff'
    },
    candlelight: {
        intensity: 0.8,
        envIntensity: 0.5,
        ambient: '#ffddbb',
        key: '#ffaa66',
        fill: '#662200'
    },
    custom: {
        intensity: 1.0,
        envIntensity: 0.7,
        ambient: '#ffffff',
        key: '#ffffff',
        fill: '#aaaaff'
    }
};
// Diamond color mapping (GIA scale)
const DIAMOND_COLOR_TINTS = {
    'D': new THREE.Color(1, 1, 1), // Colorless
    'E': new THREE.Color(1, 1, 1),
    'F': new THREE.Color(0.99, 0.99, 0.99),
    'G': new THREE.Color(0.98, 0.98, 0.97), // Near Colorless
    'H': new THREE.Color(0.97, 0.97, 0.96),
    'I': new THREE.Color(0.96, 0.96, 0.94),
    'J': new THREE.Color(0.95, 0.95, 0.92),
    'K': new THREE.Color(0.94, 0.93, 0.88), // Faint Yellow
    'L': new THREE.Color(0.93, 0.92, 0.85),
    'M': new THREE.Color(0.92, 0.91, 0.82)
};
// Diamond Material Component
const Diamond = ({ shape, color, clarity, cut, autoRotate, proportions, materialMode = 'solid' }) => {
    const meshRef = useRef(null);
    const [selectedFacet, setSelectedFacet] = useState(null);
    // Use data-driven geometry if proportions are provided, otherwise use shape-based
    // useMemo recalculates when proportions change
    const geometry = useMemo(() => {
        if (proportions && (proportions.tablePercent || proportions.depthPercent)) {
            console.log('💎 Regenerating geometry with measurements:', proportions);
            return createDataDrivenDiamond(proportions);
        }
        return getDiamondGeometry(shape);
    }, [
        shape,
        proportions?.tablePercent,
        proportions?.depthPercent,
        proportions?.crownAngle,
        proportions?.pavilionAngle
    ]);
    // Get color tint
    const colorTint = DIAMOND_COLOR_TINTS[color] || DIAMOND_COLOR_TINTS['G'];
    // Clarity affects transmission
    const clarityMap = {
        'FL': 1.0, 'IF': 0.98, 'VVS1': 0.96, 'VVS2': 0.94,
        'VS1': 0.92, 'VS2': 0.90, 'SI1': 0.85, 'SI2': 0.80
    };
    const transmission = clarityMap[clarity] || 0.90;
    // Cut quality affects brilliance
    const cutMap = {
        'Excellent': 2.42, 'Very Good': 2.41, 'Good': 2.40, 'Fair': 2.38
    };
    const ior = cutMap[cut] || 2.42;
    // Auto-rotation animation
    useFrame((state, delta) => {
        if (meshRef.current && autoRotate) {
            meshRef.current.rotation.y += delta * 0.3;
        }
    });
    // Handle facet click
    const handleClick = (event) => {
        event.stopPropagation();
        if (event.faceIndex !== undefined && geometry.userData.facets) {
            const facetId = Math.floor(event.faceIndex);
            const facet = geometry.userData.facets[facetId];
            if (facet) {
                console.log('🔷 Facet Clicked:', {
                    id: facet.id,
                    type: facet.type,
                    section: facet.section,
                    vertices: facet.vertices,
                    normal: facet.normal,
                    area: facet.area,
                    faceIndex: event.faceIndex
                });
                setSelectedFacet(facet.id);
                // Show alert with facet info
                alert(`Facet #${facet.id}
Type: ${facet.type}
Section: ${facet.section}
Normal: [${facet.normal.map((n) => n.toFixed(3)).join(', ')}]
Area: ${facet.area.toFixed(4)}
Vertices:
  V1: [${facet.vertices[0].map((v) => v.toFixed(3)).join(', ')}]
  V2: [${facet.vertices[1].map((v) => v.toFixed(3)).join(', ')}]
  V3: [${facet.vertices[2].map((v) => v.toFixed(3)).join(', ')}]`);
            }
        }
    };
    return (_jsx("mesh", { ref: meshRef, geometry: geometry, castShadow: true, receiveShadow: true, onClick: handleClick, onPointerOver: () => { if (meshRef.current)
            meshRef.current.userData.hovered = true; }, onPointerOut: () => { if (meshRef.current)
            meshRef.current.userData.hovered = false; }, children: materialMode === 'glass' ? (
        /* Glass mode: Transparent with transmission */
        _jsx("meshPhysicalMaterial", { color: colorTint, metalness: 0, roughness: 0.05, flatShading: true, envMapIntensity: 2.5, clearcoat: 1.0, clearcoatRoughness: 0.03, reflectivity: 1.0, ior: ior, transmission: 0.6, thickness: 0.5, transparent: true, opacity: 1.0, side: THREE.DoubleSide, attenuationDistance: 0.5, attenuationColor: colorTint })) : (
        /* Solid mode: Simple standard material for debugging */
        _jsx("meshStandardMaterial", { color: "#ffffff", metalness: 0, roughness: 0.3, flatShading: false, side: THREE.DoubleSide })) }));
};
// Loading Fallback
const Loader = () => (_jsxs("mesh", { children: [_jsx("octahedronGeometry", { args: [1, 2] }), _jsx("meshStandardMaterial", { color: "#cccccc", wireframe: true })] }));
// Main Viewer Component
const DiamondViewer3D = React.forwardRef(({ shape = 'round', caratWeight = 1.0, color = 'G', clarity = 'VS1', cut = 'Excellent', tablePercent, depthPercent, crownAngle, pavilionAngle, measurements, enableAR = true, onARToggle, materialMode = 'solid' }, ref) => {
    const [lightingMode, setLightingMode] = useState('studio');
    const [autoRotate, setAutoRotate] = useState(false);
    const [showSparkles, setShowSparkles] = useState(true);
    const [arMode, setARMode] = useState(false);
    const currentLighting = LIGHTING_MODES[lightingMode];
    // Scale diamond based on carat weight (rough approximation)
    const scale = Math.pow(caratWeight, 1 / 3);
    // Gather proportions data
    const proportions = {
        tablePercent,
        depthPercent,
        crownAngle,
        pavilionAngle,
        measurements
    };
    const handleARToggle = () => {
        const newState = !arMode;
        setARMode(newState);
        onARToggle?.(newState);
    };
    // Expose debug data method via ref
    React.useImperativeHandle(ref, () => ({
        getDebugData: () => {
            // Get geometry from Diamond component
            const diamondGeometry = proportions && (proportions.tablePercent || proportions.depthPercent)
                ? createDataDrivenDiamond(proportions)
                : getDiamondGeometry(shape);
            const facetsData = diamondGeometry.userData.facets || [];
            // EDGE CONNECTIVITY ANALYSIS
            // Build edge map to find gaps
            const edgeMap = {};
            const getEdgeKey = (v1, v2) => {
                const key1 = `${v1.map(v => v.toFixed(6)).join(',')}|${v2.map(v => v.toFixed(6)).join(',')}`;
                const key2 = `${v2.map(v => v.toFixed(6)).join(',')}|${v1.map(v => v.toFixed(6)).join(',')}`;
                return [key1, key2];
            };
            facetsData.forEach((facet) => {
                const v = facet.vertices;
                // Each triangle has 3 edges
                [[v[0], v[1]], [v[1], v[2]], [v[2], v[0]]].forEach((edge) => {
                    const [k1, k2] = getEdgeKey(edge[0], edge[1]);
                    edgeMap[k1] = (edgeMap[k1] || 0) + 1;
                    edgeMap[k2] = (edgeMap[k2] || 0) + 1;
                });
            });
            // Find open edges (edges with only 1 triangle = gaps/holes)
            const openEdges = [];
            Object.entries(edgeMap).forEach(([key, count]) => {
                if (count === 1) {
                    const [v1str, v2str] = key.split('|');
                    const v1 = v1str.split(',').map(Number);
                    const v2 = v2str.split(',').map(Number);
                    openEdges.push({
                        vertex1: v1.map(v => v.toFixed(3)),
                        vertex2: v2.map(v => v.toFixed(3)),
                        yLevel: ((v1[1] + v2[1]) / 2).toFixed(3)
                    });
                }
            });
            // Group open edges by Y level to identify problem areas
            const gapsByHeight = {};
            openEdges.forEach(edge => {
                const y = edge.yLevel;
                gapsByHeight[y] = (gapsByHeight[y] || 0) + 1;
            });
            return {
                CONNECTIVITY_ISSUES: {
                    totalOpenEdges: openEdges.length,
                    status: openEdges.length === 0 ? '✅ WATERTIGHT' : `❌ ${openEdges.length} GAPS FOUND`,
                    gapsByHeight,
                    openEdges: openEdges.slice(0, 20) // First 20 gaps
                },
                NORMAL_CHECK: Object.fromEntries(Object.entries(facetsData.reduce((acc, f) => {
                    const avgY = (f.vertices[0][1] + f.vertices[1][1] + f.vertices[2][1]) / 3;
                    const normalY = f.normal[1];
                    if (!acc[f.type])
                        acc[f.type] = { count: 0, wrongCount: 0, avgHeight: 0, avgNormalY: 0 };
                    acc[f.type].count++;
                    acc[f.type].avgHeight += avgY;
                    acc[f.type].avgNormalY += normalY;
                    if ((avgY > 0 && normalY < 0) || (avgY < 0 && normalY > 0)) {
                        acc[f.type].wrongCount++;
                    }
                    return acc;
                }, {})).map(([type, data]) => [
                    type,
                    {
                        count: data.count,
                        wrongNormals: data.wrongCount,
                        status: data.wrongCount === 0 ? '✅' : `❌ ${data.wrongCount} wrong`,
                        avgHeight: (data.avgHeight / data.count).toFixed(3),
                        avgNormalY: (data.avgNormalY / data.count).toFixed(3)
                    }
                ])),
                FACET_SUMMARY: {
                    total: facetsData.length,
                    byType: Object.fromEntries(Object.entries(facetsData.reduce((acc, f) => {
                        acc[f.type] = (acc[f.type] || 0) + 1;
                        return acc;
                    }, {})))
                }
            };
        }
    }));
    return (_jsxs(ViewerContainer, { children: [_jsxs(CanvasContainer, { children: [_jsx(Canvas, { shadows: true, camera: { position: [0, 1.5, 4], fov: 45 }, gl: {
                            antialias: true,
                            alpha: true,
                            toneMapping: THREE.ACESFilmicToneMapping,
                            toneMappingExposure: 1.5,
                            outputColorSpace: THREE.SRGBColorSpace
                        }, children: _jsxs(Suspense, { fallback: _jsx(Loader, {}), children: [_jsx("ambientLight", { intensity: 0.2, color: currentLighting.ambient }), _jsx("directionalLight", { position: [5, 8, 5], intensity: currentLighting.intensity * 1.5, color: currentLighting.key, castShadow: true, "shadow-mapSize-width": 2048, "shadow-mapSize-height": 2048 }), _jsx("pointLight", { position: [-4, 4, -4], intensity: 0.8, color: currentLighting.fill }), _jsx("pointLight", { position: [4, 4, -4], intensity: 0.8, color: "#ffffff" }), _jsx("pointLight", { position: [0, -3, 3], intensity: 0.4, color: "#aaccff" }), _jsx("spotLight", { position: [0, 5, -3], intensity: 1.0, angle: 0.3, penumbra: 0.5, color: "#ffffff" }), _jsx("spotLight", { position: [3, 0, 3], intensity: 0.6, angle: 0.4, penumbra: 0.8, color: "#ffeecc" }), _jsx(Environment, { preset: "studio", environmentIntensity: currentLighting.envIntensity * 1.5 }), _jsxs("group", { scale: [scale, scale, scale], position: [0, 0, 0], children: [_jsx(Diamond, { shape: shape, color: color, clarity: clarity, cut: cut, autoRotate: autoRotate, proportions: proportions, materialMode: materialMode }), showSparkles && (_jsx(Sparkles, { count: 30, scale: 2, size: 1.5, speed: 0.3, color: "#ffffff", opacity: 0.4 }))] }), _jsx(OrbitControls, { enableZoom: true, enablePan: false, minDistance: 3, maxDistance: 10, autoRotate: autoRotate, autoRotateSpeed: 2 })] }) }), _jsxs(SizeReference, { children: [caratWeight.toFixed(2), "ct \u2022 Actual Size: ", (caratWeight * 6.5).toFixed(1), "mm"] })] }), _jsxs(ControlPanel, { children: [_jsxs(ControlSection, { children: [_jsx(ControlLabel, { children: "Lighting" }), _jsx(ButtonGroup, { children: Object.keys(LIGHTING_MODES).map(mode => (_jsx(ControlButton, { "$active": lightingMode === mode, onClick: () => setLightingMode(mode), children: mode.charAt(0).toUpperCase() + mode.slice(1) }, mode))) })] }), _jsxs(ControlSection, { children: [_jsx(ControlLabel, { children: "View Options" }), _jsxs(ButtonGroup, { children: [_jsx(ControlButton, { "$active": autoRotate, onClick: () => setAutoRotate(!autoRotate), children: autoRotate ? 'Stop Rotation' : 'Auto Rotate' }), _jsx(ControlButton, { "$active": showSparkles, onClick: () => setShowSparkles(!showSparkles), children: showSparkles ? 'Hide Sparkles' : 'Show Sparkles' })] })] }), enableAR && (_jsx(ControlSection, { children: _jsxs(ARButton, { onClick: handleARToggle, "$active": arMode, children: ["\uD83D\uDCF1 ", arMode ? 'Exit AR Mode' : 'Try On Hand (AR)'] }) })), _jsx(InfoText, { children: "\uD83D\uDDB1\uFE0F Click and drag to rotate \u2022 Scroll to zoom" })] })] }));
});
// Styled Components
const ViewerContainer = styled.div `
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  border-radius: 8px;
  overflow: hidden;
  position: relative;
`;
const CanvasContainer = styled.div `
  flex: 1;
  min-height: 500px;
  position: relative;
  background: radial-gradient(circle at center, #1a1a1a 0%, #0a0a0a 100%);

  @media (max-width: 768px) {
    min-height: 400px;
  }
`;
const SizeReference = styled.div `
  position: absolute;
  bottom: 16px;
  left: 16px;
  background: rgba(0, 0, 0, 0.7);
  color: #ffffff;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;
const ControlPanel = styled.div `
  background: rgba(10, 10, 10, 0.95);
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
`;
const ControlSection = styled.div `
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;
const ControlLabel = styled.div `
  color: #999;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  font-weight: 600;
`;
const ButtonGroup = styled.div `
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;
const ControlButton = styled.button `
  padding: 8px 16px;
  background: ${props => props.$active ? '#ffffff' : 'transparent'};
  color: ${props => props.$active ? '#000000' : '#ffffff'};
  border: 1px solid ${props => props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.3)'};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &:hover {
    background: ${props => props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.1)'};
    border-color: #ffffff;
  }
`;
const ARButton = styled(ControlButton) `
  width: 100%;
  padding: 12px 20px;
  font-size: 14px;
  background: ${props => props.$active
    ? 'linear-gradient(135deg, #ff006e 0%, #8338ec 100%)'
    : 'linear-gradient(135deg, #3a0ca3 0%, #7209b7 100%)'};
  color: #ffffff;
  border: none;
  box-shadow: 0 4px 12px rgba(131, 56, 236, 0.3);

  &:hover {
    box-shadow: 0 6px 16px rgba(131, 56, 236, 0.5);
    transform: translateY(-2px);
  }
`;
const InfoText = styled.div `
  color: #666;
  font-size: 11px;
  text-align: center;
  margin-top: 12px;
  font-style: italic;
`;
export default DiamondViewer3D;
DiamondViewer3D.displayName = 'DiamondViewer3D';
